import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import Button from '@/components/base/Button';
import EmptyState from '@/components/base/EmptyState';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { notificationsFeed, type NotifItem, type NotifTone } from '@/mocks/notifications';

type FilterType = 'all' | 'unread' | 'order' | 'alert' | 'rating' | 'package' | 'settlement';

const toneIconBg: Record<NotifTone, string> = {
  info: 'bg-info-100 text-info-600',
  primary: 'bg-primary-100 text-primary-600',
  warning: 'bg-warning-100 text-warning-700',
  accent: 'bg-accent-100 text-accent-700',
  success: 'bg-success-100 text-success-700',
  danger: 'bg-danger-100 text-danger-600',
};

const filterTabs: { id: FilterType; label: string }[] = [
  { id: 'all', label: 'الكل' },
  { id: 'unread', label: 'غير مقروء' },
  { id: 'order', label: 'الطلبات' },
  { id: 'alert', label: 'التنبيهات' },
  { id: 'rating', label: 'التقييمات' },
  { id: 'package', label: 'الباقات' },
  { id: 'settlement', label: 'التسويات' },
];

function groupByDate(items: NotifItem[]): { label: string; items: NotifItem[] }[] {
  const today = '2026-09-16';
  const yesterday = '2026-09-15';
  const groups: Record<string, NotifItem[]> = {};

  items.forEach((item) => {
    const key =
      item.date === today ? 'اليوم' : item.date === yesterday ? 'أمس' : 'سابقاً';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });

  const order = ['اليوم', 'أمس', 'سابقاً'];
  return order.filter((k) => groups[k]).map((k) => ({ label: k, items: groups[k] }));
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotifItem[]>(notificationsFeed);
  const [filter, setFilter] = useState<FilterType>('all');
  const [clearAllOpen, setClearAllOpen] = useState(false);

  const unreadCount = notifications.filter((n) => n.unread).length;

  const filtered = notifications.filter((n) => {
    if (filter === 'unread') return n.unread;
    if (filter === 'all') return true;
    return n.type === filter || (filter === 'alert' && n.type === 'driver');
  });

  const grouped = groupByDate(filtered);

  const markAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
  };

  const markRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, unread: false } : n))
    );
  };

  const deleteNotif = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
    setClearAllOpen(false);
  };

  const handleAction = (notif: NotifItem) => {
    markRead(notif.id);
    if (notif.actionLink) {
      navigate(notif.actionLink);
    }
  };

  return (
    <DashboardLayout title="الإشعارات">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-bold text-[18px] text-foreground-950">الإشعارات</h2>
            {unreadCount > 0 && (
              <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-danger-500 px-1.5 text-[11px] font-bold text-white">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button variant="soft" size="sm" icon="ri-check-double-line" onClick={markAllRead}>
                تحديد الكل كمقروء
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="danger"
                size="sm"
                icon="ri-delete-bin-line"
                onClick={() => setClearAllOpen(true)}
              >
                مسح الكل
              </Button>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            {
              icon: 'ri-notification-3-line',
              label: 'إجمالي الإشعارات',
              value: notifications.length,
              bg: 'bg-secondary-100',
              text: 'text-secondary-600',
            },
            {
              icon: 'ri-mail-unread-line',
              label: 'غير مقروء',
              value: unreadCount,
              bg: 'bg-danger-100',
              text: 'text-danger-600',
            },
            {
              icon: 'ri-alarm-warning-line',
              label: 'تنبيهات عاجلة',
              value: notifications.filter((n) => n.type === 'alert' && n.unread).length,
              bg: 'bg-warning-100',
              text: 'text-warning-700',
            },
            {
              icon: 'ri-inbox-archive-line',
              label: 'طلبات جديدة',
              value: notifications.filter((n) => n.type === 'order' && n.unread).length,
              bg: 'bg-info-100',
              text: 'text-info-600',
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-3 rounded-card border border-background-200 bg-white p-4 shadow-card"
            >
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] ${stat.bg} ${stat.text}`}
              >
                <i className={`${stat.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <div>
                <p className="text-[11px] text-foreground-400">{stat.label}</p>
                <p className="mt-0.5 text-[20px] font-bold text-foreground-950">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap items-center gap-2 rounded-card border border-background-200 bg-white p-3 shadow-card">
          {filterTabs.map((tab) => {
            const count =
              tab.id === 'all'
                ? notifications.length
                : tab.id === 'unread'
                ? unreadCount
                : notifications.filter(
                    (n) =>
                      n.type === tab.id ||
                      (tab.id === 'alert' && n.type === 'driver')
                  ).length;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setFilter(tab.id)}
                className={[
                  'flex items-center gap-1.5 whitespace-nowrap rounded-btn px-3 py-1.5 text-[12px] font-medium transition-colors',
                  filter === tab.id
                    ? 'bg-primary-500 text-white'
                    : 'border border-background-200 text-foreground-600 hover:bg-background-50',
                ].join(' ')}
              >
                {tab.label}
                {count > 0 && (
                  <span
                    className={[
                      'flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold',
                      filter === tab.id
                        ? 'bg-white/20 text-white'
                        : 'bg-background-200 text-foreground-500',
                    ].join(' ')}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Notifications list */}
        {filtered.length === 0 ? (
          <EmptyState
            icon="ri-notification-off-line"
            title={
              filter === 'unread'
                ? 'لا توجد إشعارات غير مقروءة'
                : 'لا توجد إشعارات في هذه الفئة'
            }
            message={
              filter === 'unread'
                ? 'رائع! لقد قرأت جميع إشعاراتك.'
                : 'الإشعارات الخاصة بهذه الفئة ستظهر هنا عند وصولها.'
            }
          />
        ) : (
          <div className="space-y-6">
            {grouped.map((group) => (
              <div key={group.label}>
                {/* Date separator */}
                <div className="mb-3 flex items-center gap-3">
                  <div className="h-px flex-1 bg-background-200"></div>
                  <span className="rounded-full bg-background-100 px-3 py-1 text-[11px] font-medium text-foreground-500">
                    {group.label}
                  </span>
                  <div className="h-px flex-1 bg-background-200"></div>
                </div>

                <div className="space-y-2">
                  {group.items.map((notif) => (
                    <div
                      key={notif.id}
                      className={[
                        'group relative flex items-start gap-4 rounded-card border p-4 transition-colors',
                        notif.unread
                          ? 'border-secondary-200 bg-secondary-50/50'
                          : 'border-background-200 bg-white',
                      ].join(' ')}
                    >
                      {/* Unread dot */}
                      {notif.unread && (
                        <span className="absolute right-4 top-4 flex h-2 w-2 shrink-0 rounded-full bg-danger-500"></span>
                      )}

                      {/* Icon */}
                      <span
                        className={[
                          'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                          toneIconBg[notif.tone],
                        ].join(' ')}
                      >
                        <i className={`${notif.icon} text-[18px]`} aria-hidden="true"></i>
                      </span>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <span
                            className={[
                              'font-semibold text-[13px]',
                              notif.unread ? 'text-foreground-950' : 'text-foreground-700',
                            ].join(' ')}
                          >
                            {notif.title}
                          </span>
                          <span className="shrink-0 text-[11px] text-foreground-400">
                            {notif.time}
                          </span>
                        </div>

                        <p className="mt-1 text-[12px] leading-relaxed text-foreground-500">
                          {notif.body}
                        </p>

                        {/* Actions row */}
                        <div className="mt-2.5 flex flex-wrap items-center gap-2">
                          {notif.actionLabel && (
                            <button
                              type="button"
                              onClick={() => handleAction(notif)}
                              className="inline-flex items-center gap-1 rounded-btn border border-secondary-500 px-2.5 py-1 text-[11px] font-medium text-secondary-600 transition-colors hover:bg-secondary-50"
                            >
                              {notif.actionLabel}
                              <i className="ri-arrow-left-line text-[11px]" aria-hidden="true"></i>
                            </button>
                          )}
                          {notif.unread && (
                            <button
                              type="button"
                              onClick={() => markRead(notif.id)}
                              className="inline-flex items-center gap-1 rounded-btn px-2.5 py-1 text-[11px] font-medium text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-600"
                            >
                              <i className="ri-check-line text-[12px]" aria-hidden="true"></i>
                              تحديد كمقروء
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => deleteNotif(notif.id)}
                        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-foreground-300 opacity-0 transition-all hover:bg-danger-50 hover:text-danger-500 group-hover:opacity-100"
                        aria-label="حذف الإشعار"
                      >
                        <i className="ri-close-line text-base" aria-hidden="true"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clear all confirm */}
      <ConfirmDialog
        open={clearAllOpen}
        title="مسح جميع الإشعارات"
        message="هل أنت متأكد من مسح جميع الإشعارات؟ لا يمكن التراجع عن هذا الإجراء."
        confirmLabel="مسح الكل"
        tone="danger"
        onConfirm={clearAll}
        onClose={() => setClearAllOpen(false)}
      />
    </DashboardLayout>
  );
}